import type {ProblemScope} from '../../../services/rest'
import {InvestigationStates, ROOT_PROJECT_ID} from '../../../types'
import type {BuildTypeId, Investigation, NormalizedProject, ProjectId} from '../../../types'
import type {KeyValue} from '../../../utils/object'

export const getAssigneeName = (
  investigation: Investigation | null | undefined,
  isAssignedToCurrentUser: boolean,
): string =>
  isAssignedToCurrentUser
    ? 'you'
    : investigation?.assignee.name || investigation?.assignee.username || ''
export const getAssignmentName = (investigation: Investigation | null | undefined): string => {
  const assignee = investigation?.assignee
  const assignment = investigation?.assignment
  return assignee?.username !== assignment?.user?.username
    ? assignment?.user?.name || assignment?.user?.username || ''
    : ''
}
export const getActiveInvestigations = (
  investigations: ReadonlyArray<Investigation>,
): ReadonlyArray<Investigation> =>
  investigations.filter(investigation => {
    const state = investigation.state
    return (
      state != null && state !== InvestigationStates.GIVEN_UP && state !== InvestigationStates.NONE
    )
  })

const getAllParents = (
  id: ProjectId | null | undefined,
  parents: Array<ProjectId>,
  projectsHash: KeyValue<ProjectId, NormalizedProject>,
): void => {
  const project = id != null ? projectsHash[id] : null

  if (project && id !== ROOT_PROJECT_ID) {
    parents.push(project.id)
    getAllParents(project.parentProjectId, parents, projectsHash)
  }
}

export const getEntityInCurrentScope = <
  T extends {
    scope?: Investigation['scope']
  },
>(
  projectsHash: KeyValue<ProjectId, NormalizedProject>,
  entities: ReadonlyArray<T>,
  projectId: ProjectId | null | undefined,
  buildTypeId: BuildTypeId | null | undefined,
): T | null => {
  if (projectId == null) {
    return null
  }

  const currentProjectParents: ProjectId[] = []
  getAllParents(projectsHash[projectId]?.parentProjectId, currentProjectParents, projectsHash)

  for (let i = 0; i < entities.length; i++) {
    const item = entities[i]
    const projectScope = item.scope?.project
    const buildTypesScope = item.scope?.buildTypes?.buildType

    if (
      projectScope != null &&
      (projectId === projectScope.id || currentProjectParents.includes(projectScope.id))
    ) {
      return item
    }

    if (buildTypesScope != null && buildTypesScope.length > 0) {
      for (let j = 0; j < buildTypesScope.length; j++) {
        const scopeBuildTypeId = buildTypesScope[j].id

        if (scopeBuildTypeId === buildTypeId) {
          return item
        }
      }
    }
  }

  return null
}
export const getFilteredEntities = <
  T extends {
    scope?: ProblemScope
  },
>(
  projectsHash: KeyValue<ProjectId, NormalizedProject>,
  entities: ReadonlyArray<T>,
  projectId: ProjectId | null | undefined,
  buildTypeId: BuildTypeId | null | undefined,
): ReadonlyArray<T> => {
  const result: T[] = []
  const currentProjectParents: ProjectId[] = []

  if (projectId == null) {
    return result
  }

  getAllParents(projectsHash[projectId]?.parentProjectId, currentProjectParents, projectsHash)

  for (let i = 0; i < entities.length; i++) {
    const item = entities[i]
    const projectScope = item.scope?.project
    const buildTypesScope = item.scope?.buildTypes?.buildType

    if (projectScope != null) {
      const scopeProjectParents: ProjectId[] = []
      const projectScopeId = projectScope.id
      getAllParents(projectScope.id, scopeProjectParents, projectsHash)

      if (currentProjectParents.includes(projectScopeId!)) {
        result.push(item)
      }

      if (scopeProjectParents.includes(projectId)) {
        result.push(item)
      }
    }

    if (buildTypesScope != null && buildTypesScope.length > 0) {
      for (let j = 0; j < buildTypesScope.length; j++) {
        if (buildTypesScope[j].id === buildTypeId) {
          result.push(item)
          break
        }
      }
    }
  }

  return result
}
